import {
  createColumnHelper,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronDown, ChevronRight, Filter } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'

import { Badge, Button, IconButton } from 'slab'
import type { AdminMedicalPlanListItem, Pagination } from 'utils/web_api.schemas'

import { MEDICAL_PLAN_PAGINATION } from './constants'
import { ExpandedMedicalPlanRow } from './ExpandedMedicalPlanRow'

interface MedicalPlansTableProps {
  plans?: AdminMedicalPlanListItem[]
  pagination?: Pagination
  isLoading: boolean
  error: Error | null
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  onFilterByCarrier?: (carrierName: string) => void
}

const columnHelper = createColumnHelper<AdminMedicalPlanListItem>()

function createColumns (onFilterByCarrier?: (carrierName: string) => void) {
  return [
    columnHelper.display({
      id: 'actions',
      header: () => null,
      cell: ({ row }) => {
        const handleViewDetails = () => {
          row.toggleExpanded()
        }

        return (
          <div className='flex items-center justify-center w-full'>
            <IconButton
              icon={row.getIsExpanded() ? ChevronDown : ChevronRight}
              variant='secondary'
              size='small'
              label={row.getIsExpanded() ? 'Hide Details' : 'View Details'}
              onClick={handleViewDetails}
              title={row.getIsExpanded() ? 'Hide Details' : 'View Details'}
            />
          </div>
        )
      },
      size: 50,
    }),
    columnHelper.accessor('friendly_name', {
      header: 'Plan Name',
      cell: (info) => {
        const plan = info.row.original
        const planName = plan.friendly_name || plan.display_name
        const inspectUrl = `/admin/inspect/${plan.id}`
        return (
          <a
            href={inspectUrl}
            className='font-medium text-blue-600 hover:text-blue-800 hover:underline break-words'
          >
            {planName}
          </a>
        )
      },
      enableSorting: false,
    }),
    columnHelper.accessor('carrier_name', {
      header: 'Carrier',
      cell: (info) => {
        const carrierName = info.getValue()
        return (
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='break-words'>{carrierName}</span>
            {onFilterByCarrier && (
              <IconButton
                icon={Filter}
                variant='secondary'
                size='small'
                label={`Filter by ${carrierName}`}
                onClick={() => onFilterByCarrier(carrierName)}
              />
            )}
          </div>
        )
      },
      enableSorting: false,
    }),
    columnHelper.accessor('state', {
      header: 'State',
      enableSorting: false,
    }),
    columnHelper.accessor('plan_type', {
      header: 'Plan Type',
      cell: (info) => <Badge type='neutral' bordered>{info.getValue().toUpperCase()}</Badge>,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'plan_status',
      header: 'Plan Status',
      cell: (info) => {
        const plan = info.row.original
        return (
          <div className='flex flex-wrap gap-1'>
            {plan.on_market && (
              <Badge type='neutral'>On Market</Badge>
            )}
            {plan.off_market && (
              <Badge type='success'>Off Market</Badge>
            )}
            {plan.actively_marketed && (
              <Badge type='success'>Actively Marketed</Badge>
            )}
          </div>
        )
      },
      enableSorting: false,
    }),
  ]
}

export function MedicalPlansTable ({
  plans,
  pagination,
  isLoading,
  error,
  onPageChange,
  onPerPageChange: _onPerPageChange,
  onFilterByCarrier,
}: MedicalPlansTableProps) {
  const columns = createColumns(onFilterByCarrier)
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: (pagination?.current_page || MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_NUMBER) - 1, // TanStack uses 0-based indexing
    pageSize: MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_SIZE,
  })

  useEffect(() => {
    if (pagination) {
      setPaginationState({
        pageIndex: pagination.current_page - 1,
        pageSize: MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_SIZE,
      })
    }
  }, [pagination])

  const table = useReactTable({
    data: plans || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    state: {
      pagination: paginationState,
      expanded,
    },
    onExpandedChange: setExpanded,
  })

  const handleNextPage = () => {
    if (pagination?.next_page) {
      onPageChange(pagination.next_page)
    }
  }

  const handlePreviousPage = () => {
    if (pagination?.prev_page) {
      onPageChange(pagination.prev_page)
    }
  }

  if (error) {
    return (
      <div className='text-red-600 p-4'>
        Error loading plans: {error.message}
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {/* Results count */}
      {pagination && (
        <div className='text-sm text-gray-600'>
          Showing {plans?.length || 0} of {pagination.total_records} plans
        </div>
      )}

      {/* Table */}
      <div className='border border-gray-200 rounded-lg'>
        <table className='w-full divide-y divide-gray-200 table-fixed'>
          <thead className='bg-gray-50'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className='px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                    style={
                      header.id === 'actions'
                        ? { width: '50px' }
                        : header.id === 'state'
                          ? { width: '80px' }
                          : header.id === 'plan_type'
                            ? { width: '120px' }
                            : header.id === 'plan_status'
                              ? { width: '180px' }
                              : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {isLoading
              ? (
                <tr>
                  <td colSpan={columns.length} className='px-6 py-4 text-center'>
                    <div className='flex justify-center items-center'>
                      <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900' />
                    </div>
                  </td>
                </tr>
                )
              : table.getRowModel().rows.length === 0
                ? (
                  <tr>
                    <td colSpan={columns.length} className='px-6 py-4 text-center text-gray-500'>
                      No plans found. Try adjusting your filters to see more results.
                    </td>
                  </tr>
                  )
                : (
                    table.getRowModel().rows.map((row) => (
                      <Fragment key={row.id}>
                        <tr className='hover:bg-gray-50'>
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className='px-3 py-2 sm:px-6 sm:py-4 text-xs sm:text-sm text-gray-900'
                              style={
                                cell.column.id === 'actions'
                                  ? { width: '50px' }
                                  : cell.column.id === 'state'
                                    ? { width: '80px' }
                                    : cell.column.id === 'plan_type'
                                      ? { width: '120px' }
                                      : cell.column.id === 'plan_status'
                                        ? { width: '180px' }
                                        : undefined
                              }
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                        {row.getIsExpanded() && (
                          <tr id={`expanded-${row.id}`}>
                            <td colSpan={columns.length}>
                              <ExpandedMedicalPlanRow plan={row.original} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))
                  )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className='bg-white px-4 py-3 border-t border-gray-200 sm:px-6'>
          <div className='flex items-center justify-between'>
            <div className='text-sm text-gray-700'>
              Showing page {pagination.current_page} of {pagination.total_pages} ({pagination.total_records} total {pagination.total_records === 1 ? 'plan' : 'plans'})
            </div>
            <div className='flex gap-2'>
              <Button
                variant='tertiary'
                size='small'
                onClick={handlePreviousPage}
                disabled={!pagination.prev_page}
              >
                Previous
              </Button>
              <Button
                variant='tertiary'
                size='small'
                onClick={handleNextPage}
                disabled={!pagination.next_page}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
